import React, { useMemo, useRef, useState } from 'react';
import styles from './AdminCustomerForm.module.scss';
import { Form, Formik, useFormikContext } from 'formik';
import IAdminCustomer from '../../../types/IAdminCustomer';
import { FormField } from '@tcc-online/input-components';
import { useTranslate } from 'react-translate.ts';
import { ButtonVariants } from '@tcc-online/common-components';
import { ObjectSchema, StringSchema } from 'yup';
import UserRoles, { UserRole } from '../../../../common/constants/UserRoles';
import AdminFormInput from '../../input/AdminFormInput';
import AdminButton from '../../buttons/AdminButton/AdminButton';
import allLocales from '../../../../config/locales.json';
import { LanguageService } from '../../../../services';
import { produce } from 'immer';
import AdminSelect from '../../input/AdminSelect';
import RequestService from '@tcc-online/request-service';
import IID from '../../../../types/IID';
import Email from '../../../../types/common/Email';
import useDebounce from '../../../../helpers/useDebounce';
import XIcon from 'react-bootstrap-icons/dist/icons/x';

type AdminCustomerFormProps = {
    customer: IAdminCustomer | null;
    onSubmit: (values: CustomerFormValues) => void;
    userRoles: UserRole[] | null;
};

const NAME_FIELD = 'name';
const STREET_FIELD = 'street';
const HOUSE_FIELD = 'houseNumber';
const CITY_FIELD = 'city';
const POSTAL_FIELD = 'postalCode';
const REGISTRATION_FIELD = 'registrationNumber';
const TAX_FIELD = 'taxNumber';
const LANGUAGES_FIELD = 'languages';
const PRIMARY_ADMIN = 'primaryAdmin';
// const SETTINGS = 'settings';

export type CustomerFormValues = {
    [NAME_FIELD]: string;
    [STREET_FIELD]: string;
    [HOUSE_FIELD]: string;
    [CITY_FIELD]: string;
    [POSTAL_FIELD]: string;
    [REGISTRATION_FIELD]: string;
    [TAX_FIELD]: string;
    [LANGUAGES_FIELD]: string[];
    [PRIMARY_ADMIN]?: User;
    // [SETTINGS]: any;
};

interface User extends IID, Email {}

const validationSchema = new ObjectSchema({
    [STREET_FIELD]: new StringSchema().nullable(),
    [HOUSE_FIELD]: new StringSchema().nullable(),
    [CITY_FIELD]: new StringSchema().nullable(),
    [POSTAL_FIELD]: new StringSchema().nullable(),
    [REGISTRATION_FIELD]: new StringSchema().nullable(),
    [TAX_FIELD]: new StringSchema().nullable(),
});

export default function AdminCustomerForm(props: AdminCustomerFormProps) {
    const { customer, onSubmit, userRoles } = props;
    const translate = useTranslate();
    const canEditPrimaryAdmin = useMemo(
        () =>
            hasUserAnyOfRoles(userRoles, [UserRoles.admin, UserRoles.sysAdmin]),
        [userRoles],
    );

    const initialValues: CustomerFormValues = {
        [NAME_FIELD]: customer?.name || '',
        [STREET_FIELD]: customer?.street || '',
        [HOUSE_FIELD]: customer?.houseNumber || '',
        [CITY_FIELD]: customer?.city || '',
        [POSTAL_FIELD]: customer?.postalCode || '',
        [REGISTRATION_FIELD]: customer?.registrationNumber || '',
        [TAX_FIELD]: customer?.taxNumber || '',
        [LANGUAGES_FIELD]: customer?.languages || [],
        [PRIMARY_ADMIN]: customer?.primaryAdmin || undefined,
    };

    return (
        <Formik<CustomerFormValues>
            initialValues={initialValues}
            onSubmit={submit}
            validationSchema={validationSchema}
            enableReinitialize
        >
            <Form className={styles.form}>
                <FormContent canEditPrimaryAdmin={canEditPrimaryAdmin} />
                <AdminButton
                    variant={ButtonVariants.positive}
                    type="submit"
                    embossed
                >
                    {translate('Ulozit')}
                </AdminButton>
            </Form>
        </Formik>
    );

    function submit(values: CustomerFormValues) {
        onSubmit(values);
    }
}

const languages: string[] = Object.keys(allLocales);
function FormContent(props: { canEditPrimaryAdmin: boolean }) {
    const translate = useTranslate();
    const formik = useFormikContext<CustomerFormValues>();
    const locales = formik.values[LANGUAGES_FIELD];
    const langOptions = useMemo(
        () => languages.filter((locale) => !locales.includes(locale)),
        [locales],
    );
    const [userOptions, setUserOptions] = useState<User[]>([]);
    const userOptionsRef = useRef<User[]>(userOptions);
    const debounce = useDebounce<any>();

    return (
        <>
            <FormField>
                <AdminFormInput
                    name={NAME_FIELD}
                    label={translate('Name')}
                    disabled
                    readOnly
                />
            </FormField>
            <div className={styles.group}>
                <FormField>
                    <AdminFormInput
                        name={STREET_FIELD}
                        label={translate('street')}
                    />
                </FormField>
                <FormField>
                    <AdminFormInput
                        name={HOUSE_FIELD}
                        label={translate('houseNumber')}
                    />
                </FormField>
            </div>
            <div className={styles.group}>
                <FormField>
                    <AdminFormInput
                        name={CITY_FIELD}
                        label={translate('city')}
                    />
                </FormField>
                <FormField>
                    <AdminFormInput
                        name={POSTAL_FIELD}
                        label={translate('postalCode')}
                    />
                </FormField>
            </div>
            <FormField>
                <AdminFormInput
                    name={REGISTRATION_FIELD}
                    label={translate('registrationNumber')}
                />
            </FormField>
            <FormField>
                <AdminFormInput
                    name={TAX_FIELD}
                    label={translate('taxNumber')}
                />
            </FormField>
            <FormField>
                <AdminSelect
                    label={translate('Languages')}
                    options={langOptions}
                    getOptionValue={(option: string): string => option}
                    getOptionLabel={(option: string): string => {
                        return LanguageService.getLanguageName(option);
                    }}
                    onChange={addLocale(locales)}
                    searchable
                    value={null}
                />
                {!!locales.length && (
                    <div className={styles.languages}>
                        {locales.map((locale: string) => (
                            <SelectedLanguage
                                locale={locale}
                                onDelete={deleteLocale(locales)}
                            />
                        ))}
                    </div>
                )}
            </FormField>
            <FormField>
                <AdminSelect<User>
                    label={translate('Primary Admin')}
                    options={userOptions}
                    searchable
                    searchFunction={async (options, search: string) => {
                        return await debounce(searchUsers, options, search);
                    }}
                    getOptionValue={(option: User) => option.id}
                    getOptionLabel={(option: User) => option.email}
                    async
                    onChange={async (value: string) => {
                        if (value === null) {
                            return;
                        }

                        const index: number = userOptionsRef.current.findIndex(
                            (option: User) => option.id === parseInt(value),
                        );

                        if (index >= 0) {
                            const option: User = userOptionsRef.current[index];
                            setUserOptions([option]);

                            await formik.setFieldValue(PRIMARY_ADMIN, option);
                        }
                    }}
                    value={formik.values[PRIMARY_ADMIN]}
                    disabled={!props.canEditPrimaryAdmin}
                />
            </FormField>
        </>
    );

    async function searchUsers(options: User[], search: string) {
        if (search.trim().length >= 3) {
            const response = await RequestService.get<User[]>(
                `/users?isAdmin=true`,
                {
                    search,
                },
            );
            userOptionsRef.current = response.data;
            return response.data;
        }

        return options;
    }

    function addLocale(locales: string[]) {
        return async (newLocale: string) => {
            if (locales.includes(newLocale)) {
                return;
            }

            await formik.setFieldValue(
                LANGUAGES_FIELD,
                produce(locales, (draft) => {
                    draft.push(newLocale);
                }),
            );
        };
    }

    function deleteLocale(locales: string[]) {
        return async (locale: string) => {
            await formik.setFieldValue(
                LANGUAGES_FIELD,
                produce(locales, (draft) => {
                    const index = draft.indexOf(locale);

                    if (index >= 0) {
                        draft.splice(index, 1);
                    }
                }),
            );
        };
    }
}

function SelectedLanguage(props: {
    locale: string;
    onDelete: (locale: string) => void;
}) {
    return (
        <div className={styles.lang}>
            <span>
                {LanguageService.getLanguageName(props.locale.toLowerCase())}
            </span>
            <XIcon onClick={deleteLocale(props.locale)} />
        </div>
    );

    function deleteLocale(locale: string) {
        return () => {
            props.onDelete(locale);
        };
    }
}

function hasUserAnyOfRoles(
    userRoles: UserRole[] | null,
    roles: UserRole[],
): boolean {
    if (!userRoles) {
        return false;
    }

    for (const role of roles) {
        if (userRoles?.includes(role)) {
            return true;
        }
    }

    return false;
}
